import qs from "query-string"
import post, { http } from "../common/post"
import urls from "../common/urls"
import { pushRoute } from "../common/route"
import { captureException } from "../common/error"
import { timeOffset, getReferrer } from "../common/tracking"
import { formatRecommendationResponse } from "../common/common"

const checkForNext = r => {
  if (r.status === 200) return InterviewSource.nextItem()

  if (r.data) return r.data

  return r.json()
}

const InterviewSource = {
  loginSubmit: (email, password) =>
    http(urls.loginSubmit, "POST", { email, password }),
  setPassSubmit: password => http(urls.setPass, "POST", { password }),
  requestPasswordReset: email =>
    http(urls.requestPasswordReset, "POST", { email }),
  submitPasswordReset: password =>
    http(urls.resetPassword, "POST", { password }),
  logout: () => http(urls.logout, "GET"),
  signup: email => http(urls.signup, "POST", { email }),
  activate: hash => http(urls.activate + hash, "POST"),

  submitIdentifiers: (first_name, age, sex, isself) =>
    http(urls.profile, "POST", { first_name, age, sex, isself }),
  newVisit: (name, referrer, variant, marketplaceToken, publicId) =>
    http(urls.newVisit, "POST", {
      name,
      marketplace_token: marketplaceToken,
      referrer: referrer,
      variant: variant,
      public_id: publicId,
    }).catch(err => {
      if (err.response && err.response.status === 403) {
        pushRoute("/login/")
      } else {
        throw err
      }
    }),

  userIntentCareOptions: (post_visit = false) =>
    http(
      `${urls.userIntentCareOptions}${post_visit ? "?post_visit=true" : ""}`,
      "GET"
    ),

  submitUserIntent: (
    intent,
    option,
    specialist,
    post_visit = false,
    something_else
  ) =>
    http(urls.submitUserIntent, "POST", {
      intent,
      option,
      specialist,
      post_visit,
      something_else,
    }),

  submitDiagnosisConcern: (diagnosis_of_concern, pre_filled) =>
    http(urls.submitDiagnosisConcern, "POST", {
      diagnosis_of_concern,
      pre_filled,
    }),

  submitNps: (score, comment) =>
    http(urls.submitNps, "POST", { score, comment }),
  shouldGetNps: () => http(urls.shouldGetNps, "GET", null, true, true, true),
  lastAskedNps: asked => http(urls.lastAskedNps, "POST", { asked }),

  searchAutocomplete: (query, sequence) =>
    http(urls.searchAutocomplete, "POST", { query, sequence }),
  interpretSearch: query => http(urls.interpretSearch, "POST", { query }),
  submitClarifier: answer => http(urls.submitClarifier, "POST", { answer }),
  submitLayterms: concern => http(urls.submitLayterms, "POST", { concern }),
  searchFailure: (query, interpretations) =>
    http(
      urls.submitFailedSearch,
      "POST",
      { query, interpretations },
      false,
      false
    ),

  submitAnswer: (response, token) =>
    http(urls.submitAnswer, "POST", response, false, false, true, token)
      .then(checkForNext)
      .catch(captureException),

  startInterview: () => http(urls.startInterview, "GET"),
  results: (token = "") => http(urls.results + token, "GET"),
  deleteItem: index => http(urls.item + index, "DELETE"),
  peekFollowup: () => http(urls.peekFollowup, "GET"),

  diagRanksAndPredInfo: () => http(urls.diagRanksAndPredInfo, "GET"),
  diagnosisAgreement: (token, agreement) =>
    post(urls.diagnosisFeedback, { token, agreement }),
  diagnosisHelpfulness: (token, helpfulness) =>
    post(urls.diagnosisFeedback, { token, helpfulness }),
  differentialFeedback: (token, feedback) =>
    post(urls.differentialFeedback, { token, feedback }),

  getQuestionExplanation: token =>
    http(`${urls.getQuestionExplanation}${token}`, "GET"),
  attachCase: caseId => post(urls.attachCase, { case: caseId }),

  submitFeedback: option => http(urls.submitFeedback, "POST", option),

  diagnosisSearch: (query, position) =>
    http(urls.diagnosisSearch, "POST", { query, position }),

  submitLocation: location =>
    http(urls.submitLocation, "POST", location, false, true, true),
  getVisitRecommendations: query => {
    return http(
      `${urls.getVisitRecommendations}?${qs.stringify(query)}`,
      "GET",
      {},
      false,
      true,
      true
    )
      .then(data => {
        // Transform response for consumption by components
        for (const key in data.recommendations) {
          data.recommendations[key] = data.recommendations[key]
            .filter(rec => typeof rec !== "string")
            .map(rec => {
              return formatRecommendationResponse(rec)
            })
        }
        return data
      })
      .catch(captureException)
  },

  visitRecommendationUserAction: (token, actionType) =>
    http(urls.visitRecommendationUserAction, "POST", {
      token: token,
      action_type: actionType,
    }),
  nextItem: () =>
    http(urls.nextItem, "GET", undefined, true).catch(err => {
      if (
        err.response &&
        (err.response.status === 300 || err.response.status === 303)
      )
        return err.response
      else throw err
    }),
  visitDiagnosisOfConcernRecommendations: query => {
    return http(
      `${urls.visitDiagnosisOfConcernRecommendations}?${qs.stringify(query)}`,
      "GET",
      {},
      false,
      true,
      true
    )
      .then(data => {
        // Transform response for consumption by components
        for (const key in data.recommendations) {
          data.recommendations[key] = data.recommendations[key]
            .filter(rec => typeof rec !== "string")
            .map(rec => {
              return formatRecommendationResponse(rec)
            })
        }
        return data
      })
      .catch(captureException)
  },

  permissions: () => http(urls.permissions, "GET"),
  getItems: () => http(urls.item, "GET"),
  emailResults: (email, brandName) =>
    http(urls.emailResults, "POST", {
      email,
      brand: brandName || "Buoy",
    }),
  getVisits: () => http(urls.getVisits, "GET"),
  getVisitInterview: token => http(urls.getVisitInterview + token, "GET"),
  recordAnonymous: (email, token) =>
    http(urls.recordAnonymous, "POST", { email, token }),
  customVisit: path => http(urls.customVisit + path, "GET"),
  configuredExperience: name =>
    http(`${urls.configuredExperience + name}/`, "GET"),
  brand: (param, value) =>
    http(
      `${urls.marketplaceTheme}?${param}=${value}`,
      "GET",
      {},
      false,
      true,
      false,
      "",
      true
    ),
  requestReport: (
    email,
    phone,
    brandName,
    first_name,
    last_name,
    opt_in,
    questions
  ) =>
    http(urls.requestReport, "POST", {
      brand: brandName,
      email,
      phone,
      first_name,
      last_name,
      opt_in,
      questions,
    }),
  questionFeedback: (token, reason) =>
    http(urls.questionFeedback, "POST", { token, reason }),
  submitVisitTruth: obj => http(urls.recordVisitTruth, "POST", obj),

  features: () => http(urls.features, "GET"),

  crash: error =>
    post(urls.crash, { error: error + "\n" + JSON.stringify(window.loglist) }),

  getEmployer: public_id =>
    http(`${urls.getEmployer}?public_id=${public_id}`, "GET"),
  getEmployerNames: name => http(`${urls.employerNames}?text=${name}`, "GET"),

  askFollowUp: () => http(urls.askFollowUp, "GET"),
  followUpOptIn: opt_in => http(urls.followUpOptIn, "POST", { opt_in }),

  captureEmployer: (public_id, employer) =>
    http(urls.employerCapture, "POST", { public_id, employer }),

  recordProfileInterestEmail: (public_id, email, reason) =>
    http(urls.profileInterest, "POST", { public_id, email, reason }),

  recordUserSatisfaction: (rating, reason) =>
    http(urls.recordUserSatisfaction, "POST", { rating, reason }),

  getSpecialists: query => http(urls.getSpecialist, "POST", { query }),

  startVisitTracker: () =>
    http(urls.tracking, "POST", {
      event_type: "start_visit",
      page_title: "symptom-checker",
      page_url_slug: "/symptom-checker/",
      referrer: getReferrer(),
      utc_offset: timeOffset(),
    }),
  startPageTracker: () =>
    http(urls.tracking, "POST", {
      event_type: "start_page",
      page_title: "symptom-checker",
      page_url_slug: "/symptom-checker/",
      referrer: getReferrer(),
      utc_offset: timeOffset(),
    }),
  exitPageTracker: () =>
    http(urls.tracking, "POST", {
      event_type: "exit_page",
      page_title: "symptom-checker",
      page_url_slug: "/symptom-checker/",
      referrer: getReferrer(),
      utc_offset: timeOffset(),
    }),
  startSectionTracker: section_title =>
    http(urls.tracking, "POST", {
      event_type: "start_section",
      page_title: "symptom-checker",
      page_url_slug: "/symptom-checker/",
      referrer: getReferrer(),
      utc_offset: timeOffset(),
      section_title: section_title,
    }),
  endSectionTracker: section_title =>
    http(urls.tracking, "POST", {
      event_type: "end_section",
      page_title: "symptom-checker",
      page_url_slug: "/symptom-checker/",
      referrer: getReferrer(),
      utc_offset: timeOffset(),
      section_title: section_title,
    }),
  sectionActionTracker: (section_title, action) =>
    http(urls.tracking, "POST", {
      event_type: "section_action",
      page_title: "symptom-checker",
      page_url_slug: "/symptom-checker/",
      referrer: getReferrer(),
      utc_offset: timeOffset(),
      section_title: section_title,
      action: action,
    }),
  clickLinkTracker: url =>
    http(urls.tracking, "POST", {
      event_type: "click_link",
      page_title: "symptom-checker",
      page_url_slug: "/symptom-checker/",
      referrer: getReferrer(),
      utc_offset: timeOffset(),
      link_type: "CARE",
      link_url: url,
    }),
  getDiagnosisContentPage: diagnosis =>
    http(`${urls.getDiagnosisContentPage}?diagnosis=${diagnosis}`, "GET"),

  interviewEventTracker: interview_event =>
    http(urls.tracking, "POST", {
      event_type: "interview_event",
      page_title: "symptom-checker",
      page_url_slug: "/symptom-checker/",
      referrer: getReferrer(),
      utc_offset: timeOffset(),
      interview_event: interview_event,
    }).catch(captureException),

  getRegionBasedInsuranceMessages: () =>
    http(urls.regionBasedInsuranceMessages, "GET"),
  resumeVisit: () => http(urls.resumeVisit, "GET", null, true, true, true),
}

window.Source = InterviewSource

export default InterviewSource



// WEBPACK FOOTER //
// ./assets/interview/Source.js